import { useEffect, useState } from "react";
import { api } from "../api/client";
import { PhotoLightbox } from "../components/PhotoLightbox";
import type { PhotosResponse, Ride, RidePhoto } from "../api/types";

/** Which ride is open in the full-screen viewer, and at which photo. */
type Viewing = { ride: Ride; index: number };

export function Photos() {
  const [rides, setRides] = useState<Ride[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Viewing | null>(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    api
      .get<PhotosResponse>("/public/photos")
      .then((r) => setRides(r.rides))
      .catch((e) => setError(e.message));
  }, []);

  if (error) {
    return <div style={{ padding: 32, color: "var(--ink-soft)" }}>Couldn't load photos: {error}</div>;
  }
  if (!rides) {
    return <div style={{ padding: 32, color: "var(--ink-soft)" }}>Loading…</div>;
  }

  return (
    <div>
      <section style={{ padding: isDesktop ? "56px 64px 8px" : "28px 22px 8px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "var(--accent)" }}>
          From the road
        </div>
        <h1
          style={{
            margin: "6px 0 0",
            fontFamily: "var(--display)",
            fontWeight: 400,
            fontSize: isDesktop ? "clamp(36px, 4vw, 50px)" : "clamp(30px, 8vw, 40px)",
            lineHeight: 1.04,
            letterSpacing: -1.4,
          }}
        >
          Photos from the ride
        </h1>
        <p style={{ marginTop: 10, fontSize: isDesktop ? 17 : 15, lineHeight: 1.5, color: "var(--ink-soft)", maxWidth: 560 }}>
          Shots Scott picked from each ride, paired with the route and distance synced from Strava.
        </p>
      </section>

      {rides.length === 0 ? (
        <section style={{ padding: isDesktop ? "24px 64px 64px" : "16px 22px 40px" }}>
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--ink-soft)",
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: 16,
            }}
          >
            No photos yet — check back soon.
          </div>
        </section>
      ) : (
        rides.map((ride) => (
          <RideGroup
            key={ride.rideId}
            ride={ride}
            isDesktop={isDesktop}
            onOpen={(index) => setViewing({ ride, index })}
          />
        ))
      )}

      {viewing && (
        <PhotoLightbox
          photos={viewing.ride.photos ?? []}
          startIndex={viewing.index}
          title={viewing.ride.name || "Untitled ride"}
          subtitle={rideMeta(viewing.ride)}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

/** "Aug 3 · Lake Geneva, WI · 42.1 mi" — the viewer's header line. */
function rideMeta(ride: Ride): string {
  return [rideDate(ride), ride.location, `${ride.miles.toFixed(1)} mi`].filter(Boolean).join(" · ");
}

function rideDate(ride: Ride): string {
  return ride.dateIso
    ? new Date(ride.dateIso).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";
}

function RideGroup({
  ride,
  isDesktop,
  onOpen,
}: {
  ride: Ride;
  isDesktop: boolean;
  onOpen: (index: number) => void;
}) {
  const date = rideDate(ride);
  const photos = ride.photos ?? [];
  const [hover, setHover] = useState(false);
  const openLabel = `View ${photos.length} photo${photos.length === 1 ? "" : "s"} full screen`;
  const time =
    ride.timeMin >= 60
      ? `${Math.floor(ride.timeMin / 60)}h ${Math.round(ride.timeMin % 60)}m`
      : `${Math.round(ride.timeMin)}m`;

  return (
    <section style={{ padding: isDesktop ? "28px 64px 8px" : "20px 22px 8px" }}>
      {/* The whole ride header is the "open this ride's slideshow" target. */}
      <button
        type="button"
        onClick={() => onOpen(0)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={`${openLabel} from ${ride.name || "this ride"}`}
        style={{
          display: "flex",
          width: "100%",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          textAlign: "left",
          font: "inherit",
          color: "inherit",
          background: "none",
          border: "none",
          padding: "0 0 14px",
          marginBottom: 16,
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: "var(--ink-soft)", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>
            {[date, ride.location].filter(Boolean).join(" · ")}
          </div>
          <h2 style={{ margin: "4px 0 0", fontFamily: "var(--display)", fontSize: isDesktop ? 26 : 22, fontWeight: 400, letterSpacing: -0.6 }}>
            {ride.name || "Untitled ride"}
          </h2>
          <div
            aria-hidden
            style={{
              marginTop: 4,
              fontSize: 13,
              fontWeight: 600,
              color: "var(--accent)",
              transform: hover ? "translateX(2px)" : "none",
              transition: "transform 120ms",
            }}
          >
            {openLabel} →
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <StatPill value={`${ride.miles.toFixed(1)} mi`} />
          <StatPill value={time} />
          <StatPill value={`${Math.round(ride.elevFt)} ft`} />
          {ride.paceMph > 0 && <StatPill value={`${ride.paceMph.toFixed(1)} mph`} accent />}
        </div>
      </button>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
          gap: 12,
        }}
      >
        {photos.map((p, i) => (
          <figure
            key={i}
            style={{
              margin: 0,
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: 14,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <PhotoThumb photo={p} onClick={() => onOpen(i)} />
            {p.caption && (
              <figcaption style={{ padding: "9px 12px 11px", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.35 }}>
                {p.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </section>
  );
}

/** Grid tile: opens the viewer on this exact photo. */
function PhotoThumb({ photo, onClick }: { photo: RidePhoto; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={photo.caption ? `View full screen: ${photo.caption}` : "View photo full screen"}
      style={{
        padding: 0,
        border: "none",
        background: "none",
        display: "block",
        overflow: "hidden",
        lineHeight: 0,
      }}
    >
      <img
        src={photo.url}
        alt={photo.caption || ""}
        loading="lazy"
        style={{
          width: "100%",
          aspectRatio: "4/3",
          objectFit: "cover",
          display: "block",
          transform: hover ? "scale(1.04)" : "none",
          transition: "transform 200ms ease",
        }}
      />
    </button>
  );
}

function StatPill({ value, accent }: { value: string; accent?: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        background: accent ? "var(--accent-tint)" : "var(--bg)",
        border: `1px solid ${accent ? "transparent" : "var(--line)"}`,
        color: accent ? "var(--accent)" : "var(--ink)",
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      {value}
    </span>
  );
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth > 720 : true);
  useEffect(() => {
    const f = () => setIsDesktop(window.innerWidth > 720);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return isDesktop;
}
